/* ──────────────────────────────────────────────────────────────────────────
   Jay's day, for the Status menu (2026-10-08). Not started, working, at
   lunch, finished. Each status is only offered when it fits: start once (or
   again after finishing), one lunch a day, back only from lunch, finish only
   while working, so after lunch he presses Back from lunch first. Everything
   starts over at midnight in his time zone, so forgetting to finish never
   leaves tomorrow stuck.
   ────────────────────────────────────────────────────────────────────────── */

/** The four markers (server/telegram/announce.ts writes their text). Shared by the server and the menu. */
export type Status = "start" | "lunch" | "back" | "finish";
export type DayState = "off" | "working" | "lunch" | "finished";
export type DayRecord = { status: Status | null; at: Date | null; lunchAt: Date | null };
export type Day = { state: DayState; lunchUsed: boolean };

/** YYYY-MM-DD in a time zone; the server's own when the zone is unknown. */
export function localDate(at: Date, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(at);
  } catch {
    return at.toISOString().slice(0, 10);
  }
}

export function isTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat("en", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

const STATE_AFTER: Record<Status, DayState> = { start: "working", lunch: "lunch", back: "working", finish: "finished" };

export function dayOf(record: DayRecord, timeZone: string, now = new Date()): Day {
  const today = localDate(now, timeZone);
  const state = record.status && record.at && localDate(record.at, timeZone) === today ? STATE_AFTER[record.status] : "off";
  return { state, lunchUsed: Boolean(record.lunchAt && localDate(record.lunchAt, timeZone) === today) };
}

/** Why a status can't be posted now, in words for the menu; null when it can. */
export function blockedBecause(status: Status, day: Day): string | null {
  const { state } = day;
  if (status === "start") {
    if (state === "working") return "You've already started today.";
    if (state === "lunch") return "You're at lunch. Press Back from lunch first.";
    return null;
  }
  if (status === "lunch") {
    if (state === "off") return "Start work first.";
    if (state === "lunch") return "You're already at lunch.";
    if (state === "finished") return "You've finished for the day.";
    if (day.lunchUsed) return "You've had lunch today.";
    return null;
  }
  if (status === "back") {
    if (state === "lunch") return null;
    return state === "finished" ? "You've finished for the day." : "You're not at lunch.";
  }
  if (state === "working") return null;
  if (state === "lunch") return "You're at lunch. Press Back from lunch first.";
  if (state === "finished") return "You've already finished for the day.";
  return "Start work first.";
}

// ── the notch's suggestions (web/reminders.ts) ───────────────────────────────

export type Schedule = { on: boolean; start: string; lunch: string; finish: string; lunchMinutes: number; days: number[] };
export type Suggestion = { status: Status; sticky: boolean };

/** Minutes since midnight, and the weekday 1 Monday to 7 Sunday, in a time zone. */
export function clockIn(at: Date, timeZone: string): { minutes: number; weekday: number } {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23", weekday: "short" }).formatToParts(at);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
    const weekday = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(get("weekday")) + 1;
    return { minutes: Number(get("hour")) * 60 + Number(get("minute")), weekday };
  } catch {
    return { minutes: at.getUTCHours() * 60 + at.getUTCMinutes(), weekday: ((at.getUTCDay() + 6) % 7) + 1 };
  }
}

export const toMinutes = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/**
 * What the notch should suggest now, if anything. Start, lunch and finish
 * follow the clock on the chosen days and only while they fit the day.
 * Back from lunch follows the lunch itself, any day, and stays up until it
 * is pressed: there is no reminding later for that one (Jay, 2026-10-08).
 */
export function suggestion(day: Day, lunchAt: Date | null, schedule: Schedule, timeZone: string, now = new Date()): Suggestion | null {
  if (!schedule.on) return null;
  if (day.state === "lunch") {
    return lunchAt && now.getTime() >= lunchAt.getTime() + schedule.lunchMinutes * 60_000 ? { status: "back", sticky: true } : null;
  }
  const { minutes, weekday } = clockIn(now, timeZone);
  if (!schedule.days.includes(weekday)) return null;
  const finish = toMinutes(schedule.finish);
  if (day.state === "off") return minutes >= toMinutes(schedule.start) && minutes < finish ? { status: "start", sticky: false } : null;
  if (day.state !== "working") return null;
  if (minutes >= finish) return { status: "finish", sticky: false };
  if (!day.lunchUsed && minutes >= toMinutes(schedule.lunch)) return { status: "lunch", sticky: false };
  return null;
}
